/**
 * Scan feedback: a short beep (Web Audio) + vibration on every accepted keg
 * scan, and a low buzz for a rejected one, so the crew knows to move to the
 * next keg without looking at the screen.
 *
 * iOS only lets a page make sound after a user gesture, so call
 * primeScanAudio() inside the click/tap handler that opens the scanner (the
 * overlay also re-primes on any tap inside it). Web Audio follows the iPhone
 * Ring/Silent switch: in silent mode the beep is muted (same as Ekos) — the
 * on-screen flash still shows.
 *
 * navigator.vibrate works on Android Chrome; iOS Safari has no vibration API.
 */

type AudioCtor = typeof AudioContext;

let ctx: AudioContext | null = null;

function audioCtor(): AudioCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { AudioContext?: AudioCtor; webkitAudioContext?: AudioCtor };
  return w.AudioContext || w.webkitAudioContext || null;
}

/** Create / resume the shared AudioContext. Call from a user gesture (tap). */
export function primeScanAudio(): void {
  try {
    const Ctor = audioCtor();
    if (!Ctor) return;
    if (!ctx || ctx.state === 'closed') ctx = new Ctor();
    if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
    // Old iOS unlock: start a 1-sample silent buffer inside the gesture.
    const buf = ctx.createBuffer(1, 1, 22050);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    src.start(0);
  } catch {
    // No audio is never an error for scanning.
  }
}

function tone(freq: number, startAt: number, durationS: number, type: OscillatorType, peak: number) {
  if (!ctx) return;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, startAt);
  // Quick attack/release so it clicks less.
  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.exponentialRampToValueAtTime(peak, startAt + 0.01);
  gain.gain.setValueAtTime(peak, startAt + Math.max(0.011, durationS - 0.02));
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + durationS);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(startAt);
  osc.stop(startAt + durationS + 0.02);
}

function vibrate(pattern: number | number[]) {
  try {
    const nav = typeof navigator !== 'undefined' ? (navigator as Navigator & { vibrate?: (p: number | number[]) => boolean }) : null;
    if (nav && typeof nav.vibrate === 'function') nav.vibrate(pattern);
  } catch {
    // ignore
  }
}

/** Accepted scan: one short high beep (like a handheld scanner) + 60 ms buzz. */
export function playScanAccept(): void {
  vibrate(60);
  try {
    if (!ctx) primeScanAudio();
    if (!ctx) return;
    if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
    tone(1760, ctx.currentTime + 0.005, 0.12, 'sine', 0.35);
  } catch {
    // ignore
  }
}

/** Rejected scan: two low buzzes + a double vibration. */
export function playScanReject(): void {
  vibrate([90, 60, 90]);
  try {
    if (!ctx) primeScanAudio();
    if (!ctx) return;
    if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
    const t = ctx.currentTime + 0.005;
    tone(180, t, 0.14, 'square', 0.2);
    tone(150, t + 0.2, 0.18, 'square', 0.2);
  } catch {
    // ignore
  }
}

/** Test hook: forget the shared context. */
export function resetScanAudioForTests(): void {
  ctx = null;
}
